<script>
import { mapGetters } from 'vuex';
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import { GROUP_BY_FILTER, METRIC_CHART } from './constants';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import { vi as viLocale, enUS } from 'date-fns/locale';
import { formatTime } from 'shared/utils/index.js';
import ChartStats from './components/ChartElements/ChartStats.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import MultiFieldBarChart from './components/MultiFieldBarChart.vue';
export default {
  components: { ChartStats, BarChart, MultiFieldBarChart },
  props: {
    groupBy: {
      type: Object,
      default: () => ({}),
    },
    accountSummaryKey: {
      type: String,
      default: 'getAccountSummary',
    },
    reportKeys: {
      type: Object,
      default: () => ({
        CONVERSATIONS: 'conversations_count',
        INCOMING_MESSAGES: 'incoming_messages_count',
        OUTGOING_MESSAGES: 'outgoing_messages_count',
        FIRST_RESPONSE_TIME: 'avg_first_response_time',
        RESOLUTION_TIME: 'avg_resolution_time',
        RESOLUTION_COUNT: 'resolutions_count',
        REPLY_TIME: 'reply_time',
      }),
    },
  },
  setup(props) {
    const { calculateTrend, isAverageMetricType } = useReportMetrics(
      props.accountSummaryKey
    );
    return { calculateTrend, isAverageMetricType };
  },
  computed: {
    ...mapGetters({
      accountReport: 'getAccountReports',
    }),
    dateFnsLocale() {
      const localeCode = this.$i18n?.locale || 'en';
      // Map locale code sang date-fns locale
      switch (localeCode) {
        case 'vi':
          return viLocale;
        case 'en':
        default:
          return enUS;
      }
    },
    metrics() {
      const reportKeys = Object.keys(this.reportKeys);
      const infoText = {
        FIRST_RESPONSE_TIME: this.$t(
          `REPORT.METRICS.FIRST_RESPONSE_TIME.INFO_TEXT`
        ),
        RESOLUTION_TIME: this.$t(`REPORT.METRICS.RESOLUTION_TIME.INFO_TEXT`),
      };
      return reportKeys.map(key => ({
        NAME: this.$t(`REPORT.METRICS.${key}.NAME`),
        KEY: this.reportKeys[key],
        DESC: this.$t(`REPORT.METRICS.${key}.DESC`),
        INFO_TEXT: infoText[key],
        TOOLTIP_TEXT: `REPORT.METRICS.${key}.TOOLTIP_TEXT`,
        trend: this.calculateTrend(this.reportKeys[key]),
      }));
    },
    // Các series để dùng cho MultiFieldBarChart (mỗi cái là mảng [{ value, timestamp }, ...])
    conversationsSeries() {
      return this.accountReport?.data?.[this.reportKeys.CONVERSATIONS] || [];
    },
    incomingSeries() {
      return this.accountReport?.data?.[this.reportKeys.INCOMING_MESSAGES] || [];
    },
    outgoingSeries() {
      return this.accountReport?.data?.[this.reportKeys.OUTGOING_MESSAGES] || [];
    },
    resolvedSeries() {
      return this.accountReport?.data?.[this.reportKeys.RESOLUTION_COUNT] || [];
    },
    avgFirstResponseTimeSeries() {
      const data = this.accountReport?.data?.[this.reportKeys.FIRST_RESPONSE_TIME] || [];
      return data.map(item => ({
        ...item,
        value: item.value ? (item.value / 60).toFixed(1) : 0
      }));
    },
    avgResolutionTimeSeries() {
      const data = this.accountReport?.data?.[this.reportKeys.RESOLUTION_TIME] || [];
      return data.map(item => ({
        ...item,
        value: item.value ? (item.value / 60).toFixed(1) : 0
      }));
    },
    // Computed properties for chart data with i18n
    messagesChartItems() {
      return [
        { 
          label: this.$t('REPORT.CHART_LABELS.CONVERSATIONS'), 
          color: '#60A5FA', 
          series: this.conversationsSeries, 
          unit: 'none' 
        },
        { 
          label: this.$t('REPORT.CHART_LABELS.INCOMING_MESSAGES'), 
          color: '#F59E0B', 
          series: this.incomingSeries, 
          unit: 'none' 
        },
        { 
          label: this.$t('REPORT.CHART_LABELS.OUTGOING_MESSAGES'), 
          color: '#64748B', 
          series: this.outgoingSeries, 
          unit: 'none' 
        },
        { 
          label: this.$t('REPORT.CHART_LABELS.RESOLUTION_COUNT'), 
          color: '#FB7185', 
          series: this.resolvedSeries, 
          unit: 'none' 
        },
      ];
    },
    timeChartItems() {
      return [
        { 
          label: this.$t('REPORT.CHART_LABELS.FIRST_RESPONSE_TIME'), 
          color: '#12B76A', 
          series: this.avgFirstResponseTimeSeries, 
          unit: 'seconds' 
        },
        { 
          label: this.$t('REPORT.CHART_LABELS.RESOLUTION_TIME'), 
          color: '#9E77ED', 
          series: this.avgResolutionTimeSeries, 
          unit: 'seconds' 
        },
      ];
    },
  },
  methods: {
    getCollection(metric) {
      if (!this.accountReport.data[metric.KEY]) {
        return {};
      }
      const data = this.accountReport.data[metric.KEY];
      const locale = this.dateFnsLocale;
      const labels = data.map(element => {
        if (this.groupBy?.period === GROUP_BY_FILTER[2].period) {
          let week_date = new Date(fromUnixTime(element.timestamp));
          const first_day = week_date.getDate() - week_date.getDay();
          const last_day = first_day + 6;
          const week_first_date = new Date(week_date.setDate(first_day));
          const week_last_date = new Date(week_date.setDate(last_day));
          return `${format(week_first_date, 'dd-MMM', { locale })} - ${format(
            week_last_date,
            'dd-MMM',
            { locale }
          )}`;
        }
        if (this.groupBy?.period === GROUP_BY_FILTER[3].period) {
          return format(fromUnixTime(element.timestamp), 'MMM-yyyy', { locale });
        }
        if (this.groupBy?.period === GROUP_BY_FILTER[4].period) {
          return format(fromUnixTime(element.timestamp), 'yyyy', { locale });
        }
        return format(fromUnixTime(element.timestamp), 'dd-MMM', { locale });
      });
      const datasets = METRIC_CHART[metric.KEY].datasets.map(dataset => {
        switch (dataset.type) {
          case 'bar':
            return {
              ...dataset,
              yAxisID: 'y',
              label: metric.NAME,
              data: data.map(element => element.value),
            };
          case 'line':
            return {
              ...dataset,
              yAxisID: 'y',
              label: this.metrics[0].NAME,
              data: data.map(element => element.count),
            };
          default:
            return dataset;
        }
      });
      return {
        labels,
        datasets,
      };
    },
    getChartOptions(metric) {
      let tooltips = {};
      if (this.isAverageMetricType(metric.KEY)) {
        tooltips.callbacks = {
          label: tooltipItem => {
            return this.$t(metric.TOOLTIP_TEXT, {
              metricValue: formatTime(tooltipItem.yLabel),
              conversationCount:
                this.accountReport.data[metric.KEY][tooltipItem.index].count,
            });
          },
        };
      }
      return {
        scales: METRIC_CHART[metric.KEY].scales,
        tooltips: tooltips,
      };
    },
  },
};
</script>

<template>
  <div
    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 p-0 md:p-6 lg:px-40 lg:py-[50px] outline-1 outline outline-n-container rounded-xl dark:bg-n-vars-black-br/80 gap-4 md:gap-6 lg:gap-[140px]">
    <!-- <MultiFieldBarChart 
      :items="messagesChartItems" 
      :y-step="10" 
      :title="$t('REPORT.CHART_TITLES.MESSAGES')"
    />
    <MultiFieldBarChart 
      :items="timeChartItems" 
      :y-step="10" 
      :title="$t('REPORT.CHART_TITLES.TIME')" 
      :unit="$t('REPORT.CHART_UNITS.MINUTES')"
    /> -->
    <div v-for="metric in metrics" :key="metric.KEY" class="p-4 mb-3 rounded-md">
      <ChartStats :metric="metric" :account-summary-key="accountSummaryKey" />
      <div class="mt-4 h-72">
        <woot-loading-state v-if="accountReport.isFetching[metric.KEY]" class="text-xs"
          :message="$t('REPORT.LOADING_CHART')" />
        <div v-else class="flex items-center justify-center h-72">
          <BarChart v-if="accountReport.data[metric.KEY].length" :collection="getCollection(metric)"
            :chart-options="getChartOptions(metric)" />
          <span v-else class="text-sm text-slate-600">
            {{ $t('REPORT.NO_ENOUGH_DATA') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>